import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, Eye, LogOut, ExternalLink, X, Upload, Check, Image } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Brand Form Component
const BrandForm = ({ brand, onSave, onClose, authHeader }) => {
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    short_description: '',
    about: '',
    logo: '',
    gallery: [],
    franchise_fee: '',
    royalty_fee: '',
    total_investment: '',
    space_required: '',
    established: '',
    headquarters: '',
    global_outlets: '',
    website: '',
    email: '',
    contact_person: '',
    contact_number: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    key_highlights: [],
    franchise_support: [],
    ideal_for: [],
    is_featured: false,
    show_contact_info: true
  });
  const [newHighlight, setNewHighlight] = useState('');
  const [newSupport, setNewSupport] = useState('');
  const [newIdealFor, setNewIdealFor] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const logoInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    if (brand) {
      setFormData({
        ...brand,
        key_highlights: brand.key_highlights || [],
        franchise_support: brand.franchise_support || [],
        ideal_for: brand.ideal_for || [],
        gallery: brand.gallery || []
      });
    }
  }, [brand]);

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataImg = new FormData();
    formDataImg.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload-image`, formDataImg, {
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (type === 'logo') {
        setFormData({ ...formData, logo: response.data.image_url });
      } else if (type === 'gallery') {
        if (formData.gallery.length < 5) {
          setFormData({ ...formData, gallery: [...formData.gallery, response.data.image_url] });
        } else {
          toast.error('Maximum 5 gallery images allowed');
        }
      }
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const removeGalleryImage = (index) => {
    const newGallery = formData.gallery.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery: newGallery });
  };

  const addListItem = (field, value, setValue) => {
    if (value.trim()) {
      setFormData({ ...formData, [field]: [...formData[field], value.trim()] });
      setValue('');
    }
  };

  const removeListItem = (field, index) => {
    setFormData({ ...formData, [field]: formData[field].filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (brand) {
        await axios.put(`${API}/admin/brands/${brand.id}`, formData, {
          headers: { 'Authorization': authHeader }
        });
        toast.success('Brand updated successfully');
      } else {
        await axios.post(`${API}/admin/brands`, formData, {
          headers: { 'Authorization': authHeader }
        });
        toast.success('Brand created successfully');
      }
      onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save brand');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-h-[70vh] overflow-y-auto p-1" data-testid="brand-form">
      {/* Basic Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Brand Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="input-dark w-full rounded-lg"
            required
            data-testid="input-brand-name"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Category *</label>
          <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger className="input-dark w-full rounded-lg" data-testid="select-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-border">
              <SelectItem value="Food and Beverage" className="text-white hover:bg-surface-highlight">Food and Beverage</SelectItem>
              <SelectItem value="Education" className="text-white hover:bg-surface-highlight">Education</SelectItem>
              <SelectItem value="Retail" className="text-white hover:bg-surface-highlight">Retail</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Logo Upload */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Logo *</label>
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg bg-surface-highlight flex items-center justify-center overflow-hidden">
            {formData.logo ? (
              <img src={formData.logo} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <span className="text-white/30 text-2xl font-bold">{formData.name.charAt(0) || '?'}</span>
            )}
          </div>
          <input
            type="file"
            ref={logoInputRef}
            onChange={(e) => handleImageUpload(e, 'logo')}
            accept="image/*"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => logoInputRef.current?.click()}
            className="btn-gold text-sm px-4 py-2 flex items-center gap-2"
            data-testid="upload-logo-btn"
          >
            <Upload className="w-4 h-4" /> Upload Logo
          </button>
        </div>
      </div>

      {/* Gallery Images */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Gallery Images</label>
        <div className="flex gap-3 flex-wrap mb-3">
          {formData.gallery.map((img, index) => (
            <div key={index} className="relative w-24 h-24 rounded-lg overflow-hidden">
              <img src={img} alt={`Gallery ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeGalleryImage(index)}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-error text-white flex items-center justify-center"
                data-testid={`remove-gallery-${index}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <input
          type="file"
          ref={galleryInputRef}
          onChange={(e) => handleImageUpload(e, 'gallery')}
          accept="image/*"
          className="hidden"
        />
        {formData.gallery.length < 5 && (
          <button
            type="button"
            onClick={() => galleryInputRef.current?.click()}
            className="btn-gold text-sm px-4 py-2 flex items-center gap-2"
            data-testid="add-gallery-btn"
          >
            <Plus className="w-4 h-4" /> Add Gallery Images
          </button>
        )}
        <p className="text-white/40 text-xs mt-2">Upload multiple images for the brand gallery</p>
      </div>

      {/* Investment Info */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Franchise Fee</label>
          <input
            type="text"
            value={formData.franchise_fee}
            onChange={(e) => setFormData({ ...formData, franchise_fee: e.target.value })}
            className="input-dark w-full rounded-lg text-sm"
            placeholder="SGD 25,000"
            data-testid="input-franchise-fee"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Royalty Fee</label>
          <input
            type="text"
            value={formData.royalty_fee}
            onChange={(e) => setFormData({ ...formData, royalty_fee: e.target.value })}
            className="input-dark w-full rounded-lg text-sm"
            placeholder="5% of gross sales"
            data-testid="input-royalty-fee"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Initial Investment</label>
          <input
            type="text"
            value={formData.total_investment}
            onChange={(e) => setFormData({ ...formData, total_investment: e.target.value })}
            className="input-dark w-full rounded-lg text-sm"
            placeholder="SGD 150,000 - 250,000"
            data-testid="input-total-investment"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Space Required</label>
          <input
            type="text"
            value={formData.space_required}
            onChange={(e) => setFormData({ ...formData, space_required: e.target.value })}
            className="input-dark w-full rounded-lg text-sm"
            placeholder="80 - 150 sqm"
            data-testid="input-space-required"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Established</label>
          <input
            type="text"
            value={formData.established}
            onChange={(e) => setFormData({ ...formData, established: e.target.value })}
            className="input-dark w-full rounded-lg text-sm"
            placeholder="2009"
            data-testid="input-established"
          />
        </div>
      </div>

      {/* Short Description */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Short Description *</label>
        <textarea
          value={formData.short_description}
          onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
          className="input-dark w-full rounded-lg h-20 resize-none"
          required
          data-testid="input-short-description"
        />
      </div>

      {/* About */}
      <div>
        <label className="block text-white/60 text-sm mb-2">About (Detailed)</label>
        <textarea
          value={formData.about}
          onChange={(e) => setFormData({ ...formData, about: e.target.value })}
          className="input-dark w-full rounded-lg h-32 resize-none"
          data-testid="input-about"
        />
      </div>

      {/* Company Info */}
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Headquarters</label>
          <input
            type="text"
            value={formData.headquarters}
            onChange={(e) => setFormData({ ...formData, headquarters: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="Bangkok, Thailand"
            data-testid="input-headquarters"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Total Outlets</label>
          <input
            type="text"
            value={formData.global_outlets}
            onChange={(e) => setFormData({ ...formData, global_outlets: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="45+"
            data-testid="input-global-outlets"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Website</label>
          <input
            type="text"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="www.example.com"
            data-testid="input-website"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Email</label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="franchise@example.com"
            data-testid="input-email"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Contact Person</label>
          <input
            type="text"
            value={formData.contact_person}
            onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="Khun Somchai Thanakit"
            data-testid="input-contact-person"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Contact Number</label>
          <input
            type="text"
            value={formData.contact_number}
            onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="+66 2 123 4567"
            data-testid="input-contact-number"
          />
        </div>
      </div>

      {/* Social Media */}
      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <label className="block text-white/60 text-sm mb-2">Facebook URL</label>
          <input
            type="url"
            value={formData.facebook_url}
            onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="https://facebook.com/..."
            data-testid="input-facebook"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">Instagram URL</label>
          <input
            type="url"
            value={formData.instagram_url}
            onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="https://instagram.com/..."
            data-testid="input-instagram"
          />
        </div>
        <div>
          <label className="block text-white/60 text-sm mb-2">TikTok URL</label>
          <input
            type="url"
            value={formData.tiktok_url}
            onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
            className="input-dark w-full rounded-lg"
            placeholder="https://tiktok.com/..."
            data-testid="input-tiktok"
          />
        </div>
      </div>

      {/* Key Highlights */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Key Highlights</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newHighlight}
            onChange={(e) => setNewHighlight(e.target.value)}
            className="input-dark flex-1 rounded-lg"
            placeholder="Add a highlight"
            data-testid="input-new-highlight"
          />
          <button
            type="button"
            onClick={() => addListItem('key_highlights', newHighlight, setNewHighlight)}
            className="btn-gold px-4"
          >
            Add
          </button>
        </div>
        <div className="space-y-2">
          {formData.key_highlights.map((item, index) => (
            <div key={index} className="flex items-center gap-2 bg-surface-highlight rounded-lg px-4 py-2">
              <span className="flex-1 text-white text-sm">{item}</span>
              <button
                type="button"
                onClick={() => removeListItem('key_highlights', index)}
                className="text-error hover:text-error/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Franchise Support */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Franchise Support</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newSupport}
            onChange={(e) => setNewSupport(e.target.value)}
            className="input-dark flex-1 rounded-lg"
            placeholder="Add support item"
            data-testid="input-new-support"
          />
          <button
            type="button"
            onClick={() => addListItem('franchise_support', newSupport, setNewSupport)}
            className="btn-gold px-4"
          >
            Add
          </button>
        </div>
        <div className="space-y-2">
          {formData.franchise_support.map((item, index) => (
            <div key={index} className="flex items-center gap-2 bg-surface-highlight rounded-lg px-4 py-2">
              <span className="flex-1 text-white text-sm">{item}</span>
              <button
                type="button"
                onClick={() => removeListItem('franchise_support', index)}
                className="text-error hover:text-error/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Ideal For */}
      <div>
        <label className="block text-white/60 text-sm mb-2">Ideal For</label>
        <div className="flex gap-2 mb-2">
          <input
            type="text"
            value={newIdealFor}
            onChange={(e) => setNewIdealFor(e.target.value)}
            className="input-dark flex-1 rounded-lg"
            placeholder="Add target audience"
            data-testid="input-new-idealfor"
          />
          <button
            type="button"
            onClick={() => addListItem('ideal_for', newIdealFor, setNewIdealFor)}
            className="btn-gold px-4"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.ideal_for.map((item, index) => (
            <div key={index} className="flex items-center gap-2 bg-surface-highlight rounded-full px-4 py-2">
              <span className="text-white text-sm">{item}</span>
              <button
                type="button"
                onClick={() => removeListItem('ideal_for', index)}
                className="text-error hover:text-error/80"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Visibility Options */}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.is_featured}
            onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
            className="w-5 h-5 rounded bg-surface-highlight border-border text-accent focus:ring-accent"
            data-testid="checkbox-featured"
          />
          <span className="text-white">Featured Brand</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.show_contact_info}
            onChange={(e) => setFormData({ ...formData, show_contact_info: e.target.checked })}
            className="w-5 h-5 rounded bg-surface-highlight border-border text-accent focus:ring-accent"
            data-testid="checkbox-show-contact"
          />
          <div>
            <span className="text-white">Show Contact Info</span>
            <p className="text-white/40 text-xs">Toggle visibility of contact name, number & email on frontend</p>
          </div>
        </label>
      </div>

      {/* Submit */}
      <div className="flex justify-end gap-4 pt-4 border-t border-border">
        <button type="button" onClick={onClose} className="px-6 py-2 text-white/60 hover:text-white transition-colors">
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="btn-gold-filled rounded-lg flex items-center gap-2 disabled:opacity-50"
          data-testid="save-brand-btn"
        >
          {isLoading ? 'Saving...' : (brand ? 'Update Brand' : 'Add Brand')}
        </button>
      </div>
    </form>
  );
};

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [previewBrand, setPreviewBrand] = useState(null);
  const [activeTab, setActiveTab] = useState('brands');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const [galleryCaption, setGalleryCaption] = useState('');
  const galleryFileRef = useRef(null);
  const { logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const authHeader = getAuthHeader();

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/admin/brands`, {
        headers: { 'Authorization': authHeader }
      });
      setBrands(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/admin/login');
      }
      toast.error('Failed to fetch brands');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  useEffect(() => {
    if (activeTab === 'gallery') fetchGallery();
  }, [activeTab]);

  const handleDelete = async (brandId) => {
    try {
      await axios.delete(`${API}/admin/brands/${brandId}`, {
        headers: { 'Authorization': authHeader }
      });
      toast.success('Brand deleted successfully');
      fetchBrands();
    } catch (error) {
      toast.error('Failed to delete brand');
    }
  };

  // Gallery functions
  const fetchGallery = async () => {
    setGalleryLoading(true);
    try {
      const response = await axios.get(`${API}/admin/gallery`, {
        headers: { 'Authorization': authHeader }
      });
      setGalleryImages(response.data);
    } catch (error) {
      toast.error('Failed to fetch gallery');
    } finally {
      setGalleryLoading(false);
    }
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGalleryUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', galleryCaption);
      formData.append('sort_order', String(galleryImages.length));
      await axios.post(`${API}/admin/gallery`, formData, {
        headers: { 'Authorization': authHeader, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded to gallery');
      setGalleryCaption('');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setGalleryUploading(false);
      if (galleryFileRef.current) galleryFileRef.current.value = '';
    }
  };

  const handleGalleryDelete = async (imageId) => {
    try {
      await axios.delete(`${API}/admin/gallery/${imageId}`, {
        headers: { 'Authorization': authHeader }
      });
      toast.success('Gallery image deleted');
      fetchGallery();
    } catch (error) {
      toast.error('Failed to delete gallery image');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const openAddForm = () => {
    setEditingBrand(null);
    setIsFormOpen(true);
  };

  const openEditForm = (brand) => {
    setEditingBrand(brand);
    setIsFormOpen(true);
  };

  const handleFormSave = () => {
    setIsFormOpen(false);
    setEditingBrand(null);
    fetchBrands();
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-primary border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-xl font-bold text-white">Admin Dashboard</h1>
            <p className="text-white/60 text-sm">Manage brands and gallery</p>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              target="_blank"
              className="text-white/60 hover:text-white flex items-center gap-2 text-sm transition-colors"
              data-testid="view-site-link"
            >
              View Site <ExternalLink className="w-4 h-4" />
            </Link>
            {activeTab === 'brands' && (
              <button
                onClick={openAddForm}
                className="btn-gold-filled rounded-lg flex items-center gap-2"
                data-testid="add-brand-btn"
              >
                <Plus className="w-4 h-4" /> ADD NEW BRAND
              </button>
            )}
            <button
              onClick={handleLogout}
              className="w-10 h-10 rounded-lg bg-surface-highlight flex items-center justify-center text-white/60 hover:text-white transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex gap-0">
          <button
            onClick={() => setActiveTab('brands')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'brands' ? 'text-accent border-accent' : 'text-white/60 border-transparent hover:text-white'}`}
            data-testid="tab-brands"
          >
            Brands
          </button>
          <button
            onClick={() => setActiveTab('gallery')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${activeTab === 'gallery' ? 'text-accent border-accent' : 'text-white/60 border-transparent hover:text-white'}`}
            data-testid="tab-gallery"
          >
            Gallery
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {activeTab === 'brands' ? (
          <>
            {loading ? (
          <div className="text-center text-white/60 py-16">Loading brands...</div>
        ) : brands.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-white/60 mb-4">No brands added yet.</p>
            <button onClick={openAddForm} className="btn-gold inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Add Your First Brand
            </button>
          </div>
        ) : (
          <div className="card-dark rounded-xl overflow-hidden">
            <table className="admin-table">
              <thead>
                <tr className="bg-surface-highlight">
                  <th>Brand</th>
                  <th>Category</th>
                  <th>Investment</th>
                  <th>Featured</th>
                  <th>Show Contact</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {brands.map((brand) => (
                  <tr key={brand.id} data-testid={`brand-row-${brand.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-surface-highlight flex items-center justify-center overflow-hidden">
                          {brand.logo ? (
                            <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                          ) : (
                            <span className="text-accent font-bold">{brand.name.charAt(0)}</span>
                          )}
                        </div>
                        <span className="text-white font-medium">{brand.name}</span>
                      </div>
                    </td>
                    <td className="text-white/70">{brand.category}</td>
                    <td className="text-accent">{brand.total_investment || '-'}</td>
                    <td>
                      {brand.is_featured ? (
                        <span className="badge badge-success"><Check className="w-3 h-3 mr-1" /> Yes</span>
                      ) : (
                        <span className="text-white/40">No</span>
                      )}
                    </td>
                    <td>
                      {brand.show_contact_info !== false ? (
                        <span className="badge badge-success"><Eye className="w-3 h-3 mr-1" /> Shown</span>
                      ) : (
                        <span className="badge badge-warning">Hidden</span>
                      )}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => openEditForm(brand)}
                          className="w-8 h-8 rounded-lg bg-surface-highlight flex items-center justify-center text-accent hover:bg-accent hover:text-primary transition-colors"
                          data-testid={`edit-brand-${brand.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button
                              className="w-8 h-8 rounded-lg bg-surface-highlight flex items-center justify-center text-error hover:bg-error hover:text-white transition-colors"
                              data-testid={`delete-brand-${brand.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-primary border-border">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="text-white">Delete Brand</AlertDialogTitle>
                              <AlertDialogDescription className="text-white/60">
                                Are you sure you want to delete "{brand.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-surface-highlight text-white border-border hover:bg-surface hover:text-white">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDelete(brand.id)}
                                className="bg-error text-white hover:bg-error/80"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        ) : (
          /* Gallery Tab */
          <div data-testid="gallery-management">
            {/* Upload Section */}
            <div className="card-dark rounded-xl p-6 mb-8 border border-border">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-accent" /> Upload Gallery Image
              </h3>
              <div className="flex flex-col sm:flex-row gap-4">
                <input
                  type="text"
                  placeholder="Caption (optional)"
                  value={galleryCaption}
                  onChange={(e) => setGalleryCaption(e.target.value)}
                  className="input-dark flex-1 rounded-lg"
                  data-testid="gallery-caption-input"
                />
                <input
                  ref={galleryFileRef}
                  type="file"
                  accept="image/*"
                  onChange={handleGalleryUpload}
                  className="hidden"
                  data-testid="gallery-file-input"
                />
                <button
                  onClick={() => galleryFileRef.current?.click()}
                  disabled={galleryUploading}
                  className="btn-gold-filled rounded-lg flex items-center gap-2 disabled:opacity-50 whitespace-nowrap"
                  data-testid="gallery-upload-btn"
                >
                  <Upload className="w-4 h-4" />
                  {galleryUploading ? 'Uploading...' : 'Choose & Upload Image'}
                </button>
              </div>
            </div>

            {/* Gallery Grid */}
            {galleryLoading ? (
              <div className="text-center text-white/60 py-16">Loading gallery...</div>
            ) : galleryImages.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-white/60 mb-4">No gallery images yet.</p>
                <button
                  onClick={() => galleryFileRef.current?.click()}
                  className="btn-gold inline-flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" /> Upload First Image
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {galleryImages.map((img) => (
                  <div key={img.id} className="group relative aspect-square rounded-lg overflow-hidden border border-border" data-testid={`gallery-item-${img.id}`}>
                    <img src={img.image_url} alt={img.caption || 'Gallery'} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => handleGalleryDelete(img.id)}
                        className="w-10 h-10 rounded-full bg-red-500/80 flex items-center justify-center text-white hover:bg-red-500 transition-colors"
                        data-testid={`gallery-delete-${img.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    {img.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1">
                        <p className="text-white text-xs truncate">{img.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Brand Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-4xl bg-primary border-border" data-testid="brand-form-modal">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-2xl">
              {editingBrand ? 'Edit Brand' : 'Add New Brand'}
            </DialogTitle>
          </DialogHeader>
          <BrandForm
            brand={editingBrand}
            onSave={handleFormSave}
            onClose={() => setIsFormOpen(false)}
            authHeader={authHeader}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
